import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {DetectMethodChanges} from '../utils';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {PhaSheet} from './pha-sheet.model';
import {BenchmarkFit, BenchmarkHistory, BenchmarkHistoryProgress, BenchmarkInput, BenchmarkJob, BenchmarkPositionRequest, BenchmarkProgressEntry, BenchmarkReport, BenchmarkSnapshot} from './pha-benchmark.model';
import {CancelPhaBenchmarkShell, GetPhaBenchmarkJobShell, StartPhaBenchmarkShell} from './pha-benchmark-history.operation';

@Component({selector: 'ets-pha-benchmark-dialog', templateUrl: './pha-benchmark-dialog.component.html',
    styleUrls: ['./pha-benchmark-dialog.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush})
export class PhaBenchmarkDialogComponent implements OnChanges, OnDestroy {
    constructor(private readonly _changeDetector: ChangeDetectorRef, private readonly _shell: ShellClientService,
                private readonly _quotes: LastQuoteCacheService) {}
    @Input() sheet: PhaSheet;
    @Input() designKey: string;
    readonly periods = [
        {months: 3, label: '3 months'}, {months: 6, label: '6 months'}, {months: 12, label: '1 year'},
        {months: 36, label: '3 years'}, {months: 60, label: '5 years'}
    ];
    readonly limits = [1, 2, 3, 4, 5];
    months = 36;
    maximum = 3;
    visible = false;
    busy = false;
    progress = '';
    message = '';
    stage = '';
    elapsedSeconds = 0;
    historiesCompleted = 0;
    historiesTotal = 0;
    subsetsCompleted = 0;
    subsetsTotal = 0;
    historyProgress: BenchmarkHistoryProgress[] = [];
    progressLog: BenchmarkProgressEntry[] = [];
    snapshot: BenchmarkSnapshot = null;
    input: BenchmarkInput = null;
    report: BenchmarkReport = null;
    runSettingsText = '';
    histories: BenchmarkHistory[] = [];
    capturedAt = '';
    range: {start: string, end: string, fetchStart: string} = null;
    private run = 0;
    private destroyed = false;
    private requestId: string = null;
    private wake: () => void = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (this.visible && changes.designKey && changes.designKey.previousValue) {
            this.invalidate(''); this.snapshot = null;
        }
    }
    @DetectMethodChanges()
    open(): void {
        if (this.destroyed || this.visible) { return; }
        this.visible = true; this.message = ''; this.snapshot = null; this.report = null; this.input = null;
    }
    @DetectMethodChanges()
    close(): void { this.invalidate(''); this.visible = false; this.snapshot = null; this.histories = []; }
    @DetectMethodChanges()
    cancel(): void { this.invalidate('Comparison Cancelled.'); }
    private cancelBackend(id: string): void {
        try {
            if (id) { this._shell.processQuery(new CancelPhaBenchmarkShell(id)).catch(() => { /* abandoned jobs expire on the backend */ }); }
        } catch { /* Session may already be disconnected; backend jobs expire independently. */ }
    }
    private invalidate(message: string): void {
        ++this.run; this.cancelBackend(this.requestId); this.requestId = null;
        if (this.wake) { this.wake(); }
        this.busy = false; this.report = null; this.runSettingsText = ''; this.input = null; this.message = message; this.progress = '';
        this.stage = ''; this.elapsedSeconds = 0; this.historiesCompleted = 0; this.historiesTotal = 0;
        this.subsetsCompleted = 0; this.subsetsTotal = 0; this.historyProgress = []; this.progressLog = [];
    }
    private paint(): void { if (!this.destroyed) { this._changeDetector.detectChanges(); } }
    private wait(): Promise<void> {
        return new Promise(resolve => {
            const done = () => { clearTimeout(timer); this.wake = null; resolve(); };
            const timer = setTimeout(done, 1000); this.wake = done;
        });
    }
    /** Copy raw desired positions and captured quotes; Shell owns exposure rules and mathematics. */
    private positions(): BenchmarkPositionRequest[] {
        const spot = (ticker: string) => {
            const row = this.sheet.rows.find(r => r.ticker === ticker && (r.assetType === 'Stock' || r.assetType === 'Future'));
            return row ? row.price : this._quotes.getLastQuote(ticker)?.lastPx;
        };
        return [
            ...this.sheet.rows.map(r => ({label: r.displayName, ticker: r.assetType === 'Call' || r.assetType === 'Put' ? r.callUnderlying : r.ticker,
                contract: r.ticker, quantity: r.shares, side: r.side, assetType: r.assetType, expiration: r.callExpirationDate,
                price: spot(r.assetType === 'Call' || r.assetType === 'Put' ? r.callUnderlying : r.ticker), excluded: r.crossedOut})),
            ...this.sheet.premiumRows.map(r => ({label: 'Premium ' + r.displayName, ticker: r.ticker, contract: r.optionTicker,
                quantity: r.contractsSelling, side: r.side, assetType: r.optionType, expiration: r.expiration?.date,
                price: spot(r.ticker), excluded: r.crossedOut || r.removed})),
            ...this.sheet.protectionRows.map(r => ({label: 'Protection ' + r.displayName, ticker: r.symbol, contract: r.optionTicker,
                quantity: r.contracts, side: r.side, assetType: r.optionType, expiration: r.expiration?.date,
                price: spot(r.symbol), excluded: r.crossedOut || r.removed}))
        ];
    }
    @DetectMethodChanges({isAsync: true})
    async calculate(): Promise<void> {
        if (!this.visible || this.busy || this.destroyed) { return; }
        this.invalidate('');
        const months = this.months, maximum = this.maximum;
        const period = this.periods.find(p => p.months === months)?.label || months + ' months';
        const settingsText = period + ' · Up to ' + maximum + (maximum === 1 ? ' product' : ' products');
        const run = this.run, key = this.designKey;
        const cancelled = () => this.destroyed || !this.visible || run !== this.run || key !== this.designKey;
        const bytes = new Uint8Array(16); crypto.getRandomValues(bytes);
        const id = Array.from(bytes).map(x => x.toString(16).padStart(2, '0')).join('');
        this.requestId = id; this.busy = true; this.histories = []; this.snapshot = null;
        this.progress = 'Starting Backend Comparison…'; this.paint();
        try {
            let job = await this._shell.processQuery<BenchmarkJob>(new StartPhaBenchmarkShell(id, months, maximum,
                this.sheet.accountValuation.cash, this.positions()));
            while (!cancelled()) {
                if (!job || job.requestId !== id || !['running', 'complete', 'failed', 'cancelled'].includes(job.status)) {
                    throw new Error('Invalid backend comparison reply.');
                }
                this.snapshot = job.snapshot; this.input = job.input; this.report = job.report;
                this.runSettingsText = job.report ? settingsText : '';
                this.histories = job.histories || []; this.range = job.range; this.capturedAt = job.capturedAt;
                this.progress = job.progress; this.message = job.message;
                this.stage = job.stage || ''; this.elapsedSeconds = job.elapsedSeconds || 0;
                this.historiesCompleted = job.historiesCompleted || 0; this.historiesTotal = job.historiesTotal || 0;
                this.subsetsCompleted = job.subsetsCompleted || 0; this.subsetsTotal = job.subsetsTotal || 0;
                this.historyProgress = job.historyProgress || []; this.progressLog = job.progressLog || []; this.paint();
                if (job.status !== 'running') { this.requestId = null; break; }
                await this.wait(); if (cancelled()) { break; }
                job = await this._shell.processQuery<BenchmarkJob>(new GetPhaBenchmarkJobShell(id));
            }
        } catch (error) {
            if (!cancelled()) { this.message = 'Comparison Failed: ' + (error.message || 'Backend unavailable.'); this.cancelBackend(id); }
        } finally {
            // Closing can beat Start's reply; cancel again once the reply arrives.
            if (cancelled()) { this.cancelBackend(id); }
            else { this.requestId = null; this.busy = false; this.progress = ''; this.paint(); }
        }
    }
    private isSpxSingle(fit: BenchmarkFit): boolean { return fit.tickers.length === 1 && fit.tickers[0] === 'SPX'; }
    get spxBenchmark(): BenchmarkFit | null {
        // Older running backends already return individual fits; keep the rollout compatible.
        return this.report?.spxBenchmark || this.report?.singles?.find(fit => this.isSpxSingle(fit)) || null;
    }
    get spxBenchmarkMessage(): string {
        return this.report?.spxBenchmarkMessage || this.input?.excluded?.find(reason => reason.startsWith('SPX:'))
            || 'No useful positive SPX fit is available for this comparison.';
    }
    get winnerIsSpx(): boolean { return !!this.report?.best && this.isSpxSingle(this.report.best); }
    get comparisonRows(): BenchmarkFit[] {
        const winner = this.report?.best;
        const reserved = (winner ? 1 : 0) + (this.winnerIsSpx ? 0 : 1);
        return (this.report?.sizes || []).filter(fit => !this.isSpxSingle(fit)
            && !(winner && fit.tickers.length === winner.tickers.length && fit.tickers.every((ticker, i) => ticker === winner.tickers[i])))
            .sort((a, b) => (b.correlation ?? -Infinity) - (a.correlation ?? -Infinity)
                || a.residual - b.residual || a.tickers.length - b.tickers.length).slice(0, 5 - reserved);
    }
    mix(fit: BenchmarkFit): string { return fit.tickers.map((t, n) => t + ' (' + (fit.weights[n] * 100).toFixed(2) + '%)').join(' + '); }
    ngOnDestroy(): void { this.invalidate(''); this.destroyed = true; }
}
